import { useTranslation } from "react-i18next";

import type {
  ChangeAnalysis as ChangeData,
  ChangeCategory,
} from "../reportTypes";
import { CHANGE_CATEGORY_ORDER } from "../reportTypes";
import { stateColourVar } from "../stateMeta";
import { pct } from "../format";

/**
 * "Analysis of change" — the workbook's movement breakdown over whānau assessed
 * at both entry and exit. A summary improved / held / regressed bar, then the six
 * (plus regressed) movement categories with counts and shares.
 */
type Props = {
  analysis: ChangeData;
};

const IMPROVED: ChangeCategory[] = [
  "riskToProtective",
  "riskToChange",
  "changeToProtective",
];

function categoryColour(cat: ChangeCategory): string {
  if (IMPROVED.includes(cat)) return stateColourVar("protective");
  if (cat === "regressed") return stateColourVar("risk");
  return stateColourVar("change");
}

export function ChangeAnalysis({ analysis }: Props) {
  const { t } = useTranslation();
  const { total } = analysis;
  const share = (n: number) => (total > 0 ? n / total : 0);

  const summary = [
    {
      key: "improved",
      n: analysis.improved,
      colour: stateColourVar("protective"),
    },
    { key: "remained", n: analysis.remained, colour: stateColourVar("change") },
    { key: "regressed", n: analysis.regressed, colour: stateColourVar("risk") },
  ] as const;

  return (
    <div className="rpt-change">
      <div
        className="rpt-bar rpt-bar--tall"
        role="img"
        aria-label={summary
          .map((s) => `${pct(share(s.n))} ${t(`reports.summary.${s.key}`)}`)
          .join(", ")}
      >
        {summary.map((s) => {
          const w = share(s.n) * 100;
          if (w <= 0) return null;
          return (
            <span
              key={s.key}
              className="rpt-bar__seg"
              style={{ width: `${w}%`, background: s.colour }}
            >
              {w >= 10 && (
                <span className="rpt-bar__seg-label">{pct(share(s.n))}</span>
              )}
            </span>
          );
        })}
      </div>

      <ul className="rpt-change__summary">
        {summary.map((s) => (
          <li key={s.key} className="rpt-change__summary-item">
            <span className="rpt-change__summary-n" style={{ color: s.colour }}>
              {s.n}
            </span>
            <span className="rpt-change__summary-label">
              {t(`reports.summary.${s.key}`)}
            </span>
          </li>
        ))}
      </ul>

      <dl className="rpt-change__detail">
        {CHANGE_CATEGORY_ORDER.map((cat) => {
          const n = analysis.counts[cat];
          if (cat === "regressed" && n === 0) return null;
          return (
            <div key={cat} className="rpt-change__detail-row">
              <dt className="rpt-change__detail-label">
                <span
                  className="rpt-change__dot"
                  style={{ background: categoryColour(cat) }}
                  aria-hidden="true"
                />
                {t(`reports.change.${cat}`)}
              </dt>
              <dd className="rpt-change__detail-value">
                <strong>{n}</strong> <span>{pct(share(n))}</span>
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

export default ChangeAnalysis;
